import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAtom } from 'jotai/react'

import { RPCEndpointKey, SUPPORT_RPC_ENDPOINT } from '@/constants/settings'
import { staticSchems } from '@/hooks/useSchems'
import { solanaProvider } from '@/lib/solana/lib/providerFactory'
import { cn } from '@/lib/utils'
import { rpcEndpointAtom } from '@/stores/settings'

import { Flex, Grid } from '../common/Box'
import { BeautyButton, Button } from '../common/Button'
import DropdownMenu from '../common/DropdownMenu'
import { Input } from '../common/Input'
import { Dividing } from '../common/Structure'
import { ErrorText } from '../common/Text'
import { Close, Setting } from '../svgr/common'
import { Label } from '../ui/label'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'

import PreferredExplorerSelect from './PreferredExplorerSelect'
import { SubTitle, Title } from './Title'

const GlobalSettings: React.FC<{
  className?: string
  wrapperClassName?: string
}> = ({ wrapperClassName, className }) => {
  const { t } = useTranslation()
  const [rpcEndpoint, setRpcEndpoint] = useAtom(rpcEndpointAtom)
  const [selectedRpcEndpoint, setSelectedRpcEndpoint] =
    useState<string>(rpcEndpoint)
  const [invalidUrlState, setInvalidUrlState] = useState<boolean>(false)

  const [open, setOpen] = useState(false)
  const customInputRef = useRef<HTMLInputElement>(null)

  const selected = useMemo(
    () =>
      SUPPORT_RPC_ENDPOINT.find((rpc) => rpc.url === selectedRpcEndpoint)
        ?.key || RPCEndpointKey.Custom,
    [selectedRpcEndpoint]
  )

  const memoEndpointChanged = useMemo(() => {
    return rpcEndpoint !== selectedRpcEndpoint.trim()
  }, [rpcEndpoint, selectedRpcEndpoint])

  const handleRPC = useCallback(
    (key: RPCEndpointKey) => {
      if (key === RPCEndpointKey.Custom) {
        customInputRef.current?.focus()
        setSelectedRpcEndpoint('')
      } else {
        const selected = SUPPORT_RPC_ENDPOINT.find((rpc) => rpc.key === key)
        setSelectedRpcEndpoint(selected!.url)
      }
      setInvalidUrlState(false)
    },
    [setSelectedRpcEndpoint]
  )

  const handleCustomRPC = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const value = ev.target.value
      setSelectedRpcEndpoint(value)
    },
    [setSelectedRpcEndpoint]
  )

  const saveEndpointHandle = () => {
    const val = customInputRef.current?.value.trim()
    let endpoint
    try {
      endpoint = staticSchems.urlSchema.parse(val)
    } catch {
      setInvalidUrlState(true)
      return
    }
    if (endpoint !== rpcEndpoint) {
      setRpcEndpoint(endpoint)
      let endpointItem: any = SUPPORT_RPC_ENDPOINT.find(
        (item) => item.url === endpoint
      )
      if (!endpointItem) {
        endpointItem = {
          name: 'Custom',
          url: endpoint
        }
      }
      solanaProvider.switchRpc(endpointItem)
      toast.success(t('setting.message.rpcUpdated'))
    }
    setOpen(false)
  }

  useEffect(() => {
    if (open) {
      setSelectedRpcEndpoint(rpcEndpoint)
    }
  }, [open])

  return (
    <DropdownMenu
      open={open}
      onOpenChange={setOpen}
      content={{
        className: 'xl:w-[448px] w-[286px]',
        onInteractOutside: (event) => event.preventDefault()
      }}
      trigger={{
        asChild: true,
        children: (
          <BeautyButton
            wrapperClassName={wrapperClassName}
            className={className}
          >
            <Setting
              className={cn(
                'transition-transform',
                open ? 'rotate-90' : 'rotate-0'
              )}
            />
          </BeautyButton>
        )
      }}
    >
      <Grid className="gap-4 xl:gap-6">
        <Title>{t('settings')}</Title>
        <button
          type="button"
          className="fixed right-6 top-2 size-8 xl:right-4 xl:top-4"
          onClick={() => setOpen(false)}
        >
          <Close className="inline-block"></Close>
        </button>
        <Grid className="gap-4 xl:gap-4">
          <Flex className="items-center justify-between">
            <SubTitle>{t('settings.preferredExplorer')}</SubTitle>
            <PreferredExplorerSelect />
          </Flex>
          <Dividing />
          <Grid className="gap-4 xl:gap-4">
            <SubTitle>{t('settings.RPCEndpoint')}</SubTitle>
            <RadioGroup
              value={selected}
              onValueChange={(value) => handleRPC(value as RPCEndpointKey)}
              className="gap-4 xl:gap-4 xl:pl-3"
            >
              {SUPPORT_RPC_ENDPOINT.map(({ name, key }) => (
                <Flex key={key} className="items-center gap-2 xl:gap-6">
                  <RadioGroupItem
                    value={key}
                    id={key}
                    className="size-4 xl:size-6 [&_svg]:size-2 xl:[&_svg]:size-3"
                  />
                  <Label
                    htmlFor={key}
                    className="font-Orbitron text-xs xl:text-base"
                  >
                    {name}
                  </Label>
                </Flex>
              ))}
            </RadioGroup>
            <div>
              <Input
                ref={customInputRef}
                size="md"
                className="font-Orbitron"
                suffixNode={
                  <Button
                    border={false}
                    size="xs"
                    className="font-Orbitron text-xs"
                    disabled={!memoEndpointChanged}
                    onClick={saveEndpointHandle}
                  >
                    {t('common.save')}
                  </Button>
                }
                placeholder={t('settings.customRPCURL')}
                value={selectedRpcEndpoint}
                onChange={handleCustomRPC}
              />
              {invalidUrlState && (
                <ErrorText>{t('setting.message.invalidURL')}</ErrorText>
              )}
            </div>
          </Grid>
        </Grid>
      </Grid>
    </DropdownMenu>
  )
}

export default GlobalSettings
